import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Grid, Input, Modal, Typography, Switch } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import {
  useActiveTypesQuery,
  useSearchMutation,
} from '../../../../redux/services/search';
import { useNavigate } from 'react-router-dom';
import { routes } from '../../../../utils/helpers/ApiClient';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash/debounce';
import Spinner from '../../spinner/Spinner.jsx';
import { useDispatch, useSelector } from 'react-redux';
import { closeModal } from '../../../../redux/slices/searchModalSlice.jsx';
import SearchResultWrapper from '../../../pages/PublicPages/Search/SearchResults/SearchResultWrapper.jsx';
import { capitalize } from 'lodash';
import { clearFilter } from '../../../../redux/slices/searchFiltersSlice.jsx';

const { useBreakpoint } = Grid;

export default function SearchModal() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Redux state
  const { isOpen } = useSelector((state) => state.searchModal);
  const dispach = useDispatch();

  // Search query input
  const [searchQuery, setSearchQuery] = useState('');
  // State for file search switch
  const [searchFiles, setSearchFiles] = useState(true);
  // RTK Query hook
  const [search, { data, isLoading }] = useSearchMutation();
  const { data: activeTypes, isLoading: isActiveTypesIsLoading } =
    useActiveTypesQuery();

  const inputRef = useRef(null);

  // Focus the input when the modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current.focus();
      }, 200); // to give time open and render the input
    }
  }, [isOpen]);

  // Compute filtered activeTypes based on switch
  const filteredActiveTypes = React.useMemo(() => {
    if (!activeTypes || !Array.isArray(activeTypes)) {
      // If active types aren't loaded yet, respect the switch
      return searchFiles ? ['files'] : [];
    }
    // Remove all 'files' entries
    const withoutFiles = activeTypes.filter((type) => type !== 'files');
    if (searchFiles) {
      // If switch is ON, add 'files' to the BEGINNING (to ensure it's always present and not duplicated)
      return ['files', ...withoutFiles];
    }
    // If switch is OFF, do not include 'files'
    return withoutFiles;
  }, [activeTypes, searchFiles]);

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce((query) => {
      if (!query) return;
      // Example request body
      search({
        query,
        options: filteredActiveTypes,
        precision_search: false,
        limit: 5,
        number_of_highlighted_fragments: 1,
        search_after: [],
      });
    }, 300),
    [search, filteredActiveTypes],
  );

  // Re-run search when active types set changes and query isn't empty
  useEffect(() => {
    if (searchQuery && searchQuery.trim() !== '') {
      debouncedSearch(searchQuery);
    }
  }, [filteredActiveTypes, searchQuery, debouncedSearch]);

  // Handle typing in the search input
  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    debouncedSearch(value);
  };

  // Close modal
  const handleClose = () => {
    // If you want to navigate back to the homepage:
    dispach(closeModal());
  };

  const handleSelect = (url, model_type) => {
    dispach(closeModal());
    if (model_type === 'files') {
      // open on new tab
      window.open(url, '_blank');
    } else {
      navigate(url);
    }
  };

  const screens = useBreakpoint();
  const modalWidth = screens.xl ? '70%' : screens.lg ? '80%' : '95%';

  // Determine if device is mobile/tablet (not large)
  const isMobileOrTablet = !screens.lg;
  const closeButtonLabel = isMobileOrTablet ? 'X' : 'ESC';

  return (
    <Modal
      open={isOpen}
      onCancel={handleClose}
      footer={null}
      closable={false}
      width={modalWidth}
      styles={{
        mask: {
          backdropFilter: 'blur(3px)',
        },
        body: {
          background: 'transparent',
        },
      }}
    >
      {/* Top Bar: Search Input + Close Button */}
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <Input
          placeholder={t('Vyhľadať...')}
          prefix={<SearchOutlined />}
          value={searchQuery}
          onChange={handleInputChange}
          allowClear
          focus
          ref={inputRef}
        />
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            whiteSpace: 'nowrap',
          }}
        >
          <Switch
            style={{ marginLeft: 8 }}
            checked={searchFiles}
            onChange={(checked) => {
              setSearchFiles(checked);
              // search will be triggered by useEffect on filteredActiveTypes if query isn't empty
            }}
          />
          <span style={{ marginLeft: 4, whiteSpace: 'nowrap' }}>
            {t('Hľadať aj v súboroch')}
          </span>
        </div>
        <Button onClick={handleClose}> {closeButtonLabel} </Button>
      </div>

      {(isLoading || isActiveTypesIsLoading) && (
        <div
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            maxHeight: 100,
          }}
        >
          <Spinner />
        </div>
      )}

      {data && (
        <SearchResultWrapper
          onSelect={handleSelect}
          data={data}
          simpleResults
        />
      )}

      {/*{data?.hits?.length > 0 && */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: 5,
        }}
      >
        <Typography.Link
          onClick={() => {
            navigate({
              pathname: routes.search,
              search: `?q=${encodeURIComponent(searchQuery)}`,
            });
            dispach(closeModal());
            dispach(clearFilter());
          }}
          style={{ cursor: 'pointer' }}
        >
          {capitalize(t('Kompletné výsledky'))}
        </Typography.Link>
      </div>
      {/*}*/}
    </Modal>
  );
}
